import {
  isAge70OrOverByYear,
  isAgeOverByYear,
  getBirthYearFromRRN,
  getBirthDateFromRRN,
} from './charSet';

export interface FamilyDeductionMember {
  is_ele_deduct?: string;
  family_type?: string;
  res_id?: string;
  adopt_date?: string;
  is_handy?: string;
  family_seqid?: string | number;
}

/**
 * 소득자(근로자 본인) 거주자 여부. 아직 연동 전 필드라 값이 없으면 거주자로 간주(is_residential이 'N'일 때만 비거주자)
 */
export const isResidentTaxpayer = (userInfoData: any): boolean => {
  return userInfoData?.is_residential !== 'N';
};

/**
 * 경로우대공제: 기본공제 대상자 + 귀속연도 기준 만 70세 이상
 * 본인(family_type '0')이면 거주자 여부와 무관하게 적용, 본인 외에는 소득자가 거주자인 경우에만 적용
 */
export const isElderlyDeductionApplied = (
  member: FamilyDeductionMember,
  acctYear: string | number,
  residentTaxpayer: boolean
): boolean => {
  const isEleDeducted = member.is_ele_deduct === 'Y';
  const isSelf = member.family_type === '0';
  return (
    isEleDeducted &&
    (isSelf || residentTaxpayer) &&
    isAge70OrOverByYear(member.res_id || '', acctYear)
  );
};

/**
 * 장애인공제: 기본공제 대상자에 한해 적용
 */
export const isHandyDeductionApplied = (member: FamilyDeductionMember): boolean => {
  const isEleDeducted = member.is_ele_deduct === 'Y';
  return isEleDeducted && (member.is_handy || '0') !== '0';
};

/**
 * 자녀세액공제: 기본공제 대상자 + 소득자(본인) 거주자 + 직계비속 자녀·입양자(4)/위탁아동(8) + 귀속연도 기준 만 8세 이상
 */
export const isChildTaxDeductionApplied = (
  member: FamilyDeductionMember,
  acctYear: string | number,
  residentTaxpayer: boolean
): boolean => {
  const isEleDeducted = member.is_ele_deduct === 'Y';
  const isChildFamilyType = ['4', '8'].includes(member.family_type || '');
  return (
    isEleDeducted &&
    residentTaxpayer &&
    isChildFamilyType &&
    isAgeOverByYear(member.res_id || '', acctYear, 8)
  );
};

/**
 * 출산입양공제(표시 전용, 별도 저장 플래그 없음)
 * 기본공제 대상 자녀·입양자(4)/위탁아동(8)의 주민번호 출생연도가 과세연도와 같으면 적용
 */
export const isBirthOrAdoptDeductionApplied = (
  member: FamilyDeductionMember,
  acctYear: string | number
): boolean => {
  const isEleDeducted = member.is_ele_deduct === 'Y';
  const isChildFamilyType = ['4', '8'].includes(member.family_type || '');
  if (!isEleDeducted || !isChildFamilyType) return false;

  const targetYear = Number(acctYear);
  const birthYear = getBirthYearFromRRN(member.res_id || '');
  return birthYear !== null && birthYear === targetYear;
};

// 자녀구분(첫째/둘째/셋째이상) 순위 산정 대상: 직계비속 자녀·입양자(4)이면서 기본공제 대상자
const CHILD_ORDER_FAMILY_TYPE = '4';

const isChildOrderTarget = (member: FamilyDeductionMember): boolean => {
  return (
    member.family_type === CHILD_ORDER_FAMILY_TYPE &&
    member.is_ele_deduct === 'Y' &&
    getBirthDateFromRRN(member.res_id || '') !== null
  );
};

/**
 * 자녀구분(child_cd) 자동 산정: 주민번호(생년월일)로 직계비속 자녀 중 출생순서를 매겨
 * 첫째('1')/둘째('2')/셋째이상('3')을 반환. 대상이 아니면 '0'(미입력).
 * 이미 저장된 형제 목록(siblings)과 비교해 현재 자녀(current)만의 순위를 계산하며,
 * siblings 중 현재 자녀 자신의 기존 레코드는 family_seqid로 제외한다.
 */
export const computeChildOrderCode = (
  current: FamilyDeductionMember,
  siblings: FamilyDeductionMember[]
): string => {
  if (!isChildOrderTarget(current)) return '0';

  const currentBirthDate = getBirthDateFromRRN(current.res_id || '') as number;

  const olderSiblingCount = (siblings || []).filter(sibling => {
    if (
      current.family_seqid !== undefined &&
      current.family_seqid !== '' &&
      sibling.family_seqid === current.family_seqid
    ) {
      return false; // 자기 자신(기존 레코드) 제외
    }
    if (!isChildOrderTarget(sibling)) return false;

    const siblingBirthDate = getBirthDateFromRRN(sibling.res_id || '') as number;
    return siblingBirthDate < currentBirthDate;
  }).length;

  const rank = olderSiblingCount + 1;
  if (rank === 1) return '1';
  if (rank === 2) return '2';
  return '3';
};
